import React, {
    FormEvent, ReactElement, useContext, useState,
    useEffect,
    useMemo,
} from "react";
import Confirm from "../components/confirm/Confirm";
import Form, { FormCreateType, FormDisplayUserType } from "../components/form/Form";
import TimedMessage from "../components/timed-message/TimedMessage";
import { Permission, TicketInput, useCreateTicketMutation } from "../__generated__/api";
import usePortal from "./PortalContext";
import useRouting, { Link } from "./Routing";

interface CreateTicketActions {
    show(requestTypeId?: string): void;
    onChangeRequestType(handler: ((id?: string) => void)): void
}

export const CreateTicketContext = React.createContext<CreateTicketActions>({
    show() {
        // do nothing
    },
    onChangeRequestType() {
        // do nothing
    },

});

export function CreateTicketProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [isDisplayed, setIsDisplayed] = useState(false);
    const { permissions } = usePortal();
    const [ticketInput, setTicketInput] = useState<TicketInput>();
    const [createdId, setCreatedId] = useState<string | undefined>();
    const [requestTypeId, setRequestTypeId] = useState<string | undefined>();

    const [canCreate, setCanCreate] = useState(false);

    const [createTicket] = useCreateTicketMutation();
    const { Paths } = useRouting();

    const hasPermissions = permissions.has(Permission.ChatsModify)
    || permissions.has(Permission.Administrator)
    || permissions.has(Permission.TicketCreateGuest)
    || permissions.has(Permission.TicketCreatePublic);

    const handlers = useMemo<((id?: string) => void)[]>(() => [], []);
    const instance = useMemo(() => ({
        show: (typeId?: string) => {
            setIsDisplayed(true);
            if (typeId) {
                setRequestTypeId(typeId);
            }
        },
        onChangeRequestType(handler: ((id?: string) => void)): void {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
                handlers.push(handler);
                return () => {
                    const index = handlers.findIndex(item => item === handler);
                    if (index) {
                        handlers.splice(index, 0);
                    }
                };
            // eslint-disable-next-line react-hooks/exhaustive-deps
            });
        },
    }), []);

    useEffect(() => {
        setTicketInput({
            ...ticketInput,
            customFields: [],
        });
    }, [ticketInput?.formId]);

    const setTicket = (ticket: Partial<TicketInput>) => {
        let customFields = ticketInput?.customFields;
        if (ticket.customField) {
            customFields = customFields ?? [];

            customFields.push(ticket.customField);
            customFields = customFields.dedupe(field => field.formFieldId, (__, last) => last);
        }
        const copy = {
            ...ticketInput,
            ...ticket,
            customFields,
        };
        delete copy.customField;

        setTicketInput(copy);
    };

    const onCreate = async (evt?: FormEvent) => {
        if (!ticketInput) {
            evt?.preventDefault();
            return;
        }
        setIsDisplayed(false);
        const response = await createTicket({
            variables: {
                ticket: ticketInput,
            },
        });
        setCreatedId(response.data?.saveTicket.ticketNumber?.toString());
    };

    return (
        <CreateTicketContext.Provider value={instance}>
            {createdId && (
                <TimedMessage shouldShow title="Request Created" onClose={() => setCreatedId(undefined)}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            Your request has been created.
                        </div>
                        <Link
                            to={Paths.TICKETS_VIEW}
                            params={{ ticketId: createdId }}
                            className="btn btn-primary d-block btn-sm"
                            onClick={() => setCreatedId(undefined)}
                        >
                            View Request
                        </Link>
                    </div>
                </TimedMessage>
            )}
            {hasPermissions && (
                <Confirm
                    shouldShow={isDisplayed}
                    onHide={() => {
                        setIsDisplayed(false);
                        handlers.forEach(handler => handler());
                    }}
                    closeText="Close"
                    confirmText={canCreate ? "Create" : undefined}
                    onConfirm={canCreate ? (evt => onCreate(evt)) : undefined}
                >
                    <Form
                        isEditable
                        isEditing
                        isFormField
                        formId={requestTypeId}
                        canSelectRequestType
                        onChange={setTicket}
                        onChangeRequestType={(_old, newType) => {
                            handlers.forEach(handler => handler(newType?.id));
                        }}
                        selectDefaultRequestType
                        shouldShowDescriptions
                        shouldApplyRequired
                        displayUserType={FormDisplayUserType.REQUESTOR}
                        setCreateType={type => setCanCreate(type === FormCreateType.CREATABLE)}
                    />
                </Confirm>
            )}
            {children}
        </CreateTicketContext.Provider>
    );
}

export default function useCreateTicket(): CreateTicketActions {
    return useContext(CreateTicketContext) ?? {};
}
